import { ALPHABET } from './alphabet'
import type { Random } from './types'

// ALPHABET.length (63) does not evenly divide 256 (256 = 4*63 + 4), so a plain
// `byte % 63` is biased: indices 0-3 would be picked 5/256 of the time vs 4/256
// for the rest. Rejection sampling removes the bias: only accept bytes below
// the largest multiple of 63 that fits in a byte (252 = 4*63); redraw the rest.
const REJECTION_CEILING = 4 * ALPHABET.length // 252

/**
 * Draws `count` uniformly distributed indices into ALPHABET using unbiased
 * rejection sampling, reading random bytes from `random.getRandomValues`.
 * Rejected bytes just cost an extra draw from the same stream, so this also
 * works with the deterministic fakeCryptoRandom used by tests.
 */
export function sampleAlphabetIndices(random: Random, count: number): number[] {
  const indices: number[] = []
  // Ask for a few extra bytes upfront so the common case needs only one
  // getRandomValues call; top up in batches if too many bytes were rejected.
  let batchSize = count + Math.ceil(count / 8) + 4

  while (indices.length < count) {
    const bytes = random.getRandomValues(new Uint8Array(batchSize))
    for (const byte of bytes) {
      if (byte >= REJECTION_CEILING) continue
      indices.push(byte % ALPHABET.length)
      if (indices.length === count) break
    }
    batchSize = count - indices.length + 4
  }

  return indices
}

/** Same as {@link sampleAlphabetIndices} but returns the ALPHABET characters directly. */
export function sampleAlphabetChars(random: Random, count: number): string {
  let str = ''
  for (const index of sampleAlphabetIndices(random, count))
    str += ALPHABET[index]
  return str
}
